import { LogOut } from "lucide-react";
import type { ReactNode } from "react";
import { Avatar, AvatarFallback } from "./avatar";
import { Button } from "./button";
import { containerGutter } from "./page-container";
import logoMobile from "./assets/corail-marque.svg";
import logoDesktop from "./assets/corail-horizontal.svg";

export interface NavbarProps {
  /** Titre ou élément de logo */
  title: string | ReactNode;
  /** Informations d'affichage de l'utilisateur */
  userInfo?: {
    displayName: string;
    subtitle: string;
    initials: string;
  };
  /** Gestionnaire de déconnexion */
  onLogout?: () => void;
  /** Contenu additionnel à gauche (menu hamburger, recherche…) — rendu avant le logo */
  leftContent?: ReactNode;
  /** Contenu rendu juste après le logo (menu de navigation, par exemple) */
  afterLogo?: ReactNode;
  /** Contenu affiché entre le titre et les infos utilisateur (menu de navigation, par exemple) */
  centerContent?: ReactNode;
  /** Contenu additionnel à droite */
  rightContent?: ReactNode;
  /** La déconnexion est-elle en cours ? */
  isLoggingOut?: boolean;
}

export function Navbar({
  title,
  userInfo,
  onLogout,
  leftContent,
  afterLogo,
  centerContent,
  rightContent,
  isLoggingOut = false,
}: NavbarProps) {
  return (
    <header className="sticky top-0 z-40 border-b bg-card/95 shadow-2xs backdrop-blur supports-backdrop-filter:bg-card/75">
      <div className={`mx-auto grid grid-cols-[1fr_auto_1fr] w-full h-14 items-center ${containerGutter}`}>
        <div className="flex items-center gap-2">
          {leftContent}
          <img src={logoMobile} alt="Logo" className="h-8 w-auto sm:hidden" />
          <img
            src={logoDesktop}
            alt="Logo"
            className="h-8 w-auto hidden sm:block"
          />
          {afterLogo}
        </div>

        <div className="flex items-center justify-center font-semibold">
          {typeof title === "string" ? <span>{title}</span> : title}
        </div>

        <div className="flex items-center justify-end gap-2">
          {centerContent && (
            <div className="hidden md:flex items-center">{centerContent}</div>
          )}

          {rightContent}

          {userInfo && (
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-foreground">
                  {userInfo.displayName}
                </p>
                <p className="text-xs text-muted-foreground">
                  {userInfo.subtitle}
                </p>
              </div>
              <Avatar className="h-7 w-7 sm:h-8 sm:w-8 bg-primary ring-0 ring-charte-line-accent transition-all duration-150 hover:ring-2">
                <AvatarFallback className="bg-primary text-xs text-primary-foreground">
                  {userInfo.initials}
                </AvatarFallback>
              </Avatar>
            </div>
          )}

          {onLogout && (
            <Button
              variant="ghost"
              size="icon"
              aria-label="Se déconnecter"
              onClick={onLogout}
              disabled={isLoggingOut}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
